import { ServerEndpoint } from "../Redux/endpoints.ts";
import { get, post, patch, downloadFile } from "./ApiService";

const AdminService = {
    async getLogs(tail = 50, grep = '', selectedDay) {
        try {
            const tailParam = typeof tail === 'number' ? tail : parseInt(tail?.toString?.()) || 50;
            const grepParam = grep ? `&grep=${encodeURIComponent(grep)}` : '';
            const formattedDate = selectedDay ? new Date(selectedDay).toISOString().split('T')[0] : '';
            const logRangeParam = selectedDay ? `&selectedDate=${formattedDate}` : '';
            const url = `${ServerEndpoint.ADMIN_LOGS}?tail=${tailParam}${grepParam}${logRangeParam}`;
            const res = await get(url);
            return res.data.data || '';
        } catch (error) {
            console.error('Failed to fetch raw logs:', error);
            return '';
        }
    },

    async listBucketContents() {
        try {
            const res = await get(ServerEndpoint.ADMIN_BUCKET);
            return res?.data?.data || [];
        } catch (error) {
            console.error('Failed to fetch bucket contents:', error);
            return [];
        }
    },

    async sendQuery(query) {
        try {
            const res = await post(ServerEndpoint.ADMIN_QUERY, { query });
            return res?.data?.data || [];
        } catch (error) {
            console.error('Failed to execute query:', error);
            return [];
        }
    },


    async getSystemDataPaths() {
        try {
            const res = await get(ServerEndpoint.ADMIN_SYSTEM_DATA);
            return res?.data?.data || [];
        } catch (error) {
            console.error('Failed to fetch system data paths:', error);
            return [];
        }
    },

    async getSystemData() {
        try {
            const res = await get(ServerEndpoint.ADMIN_SYSTEM_DATA);
            return res?.data?.data || {};
        } catch (error) {
            console.error('Failed to fetch full system data:', error);
            return {};
        }
    },

    async getSystemDataByPath(path) {
        try {
            const res = await post(ServerEndpoint.ADMIN_SYSTEM_DATA_FIELD, { path });
            return res?.data?.data || {};
        } catch (error) {
            console.error(`Failed to fetch system data by path "${path}":`, error);
            return {};
        }
    },

    async updateSystemData(path, data) {
        try {
            const res = await post(ServerEndpoint.ADMIN_SYSTEM_DATA_EDIT, { path, data });
            return res?.data?.data || {};
        } catch (error) {
            console.error('Failed to update system data:', error);
            return {};
        }
    },

    async deleteSystemDataEntry(path, id = null) {
        try {
            const res = await post(ServerEndpoint.ADMIN_SYSTEM_DATA_DELETE, { path, id });
            return res?.data?.data || {};
        } catch (error) {
            console.error('Failed to delete system data entry:', error);
            return {};
        }
    },

    async addSystemDataEntry(path, data) {
        try {
            const res = await post(ServerEndpoint.ADMIN_SYSTEM_DATA_NEW, { path, data });
            return res?.data?.data || {};
        } catch (error) {
            console.error('Failed to add system data entry:', error);
            return {};
        }
    },

    async getOrders() {
        try {
            const res = await get(ServerEndpoint.ADMIN_ORDERS);
            return Array.isArray(res?.data?.data) ? res.data.data : [];
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            return [];
        }
    },

    async getUsers() {
        try {
            const res = await get(ServerEndpoint.ADMIN_USERS);
            return Array.isArray(res?.data?.data) ? res.data.data : [];
        } catch (error) {
            console.error('Failed to fetch users:', error);
            return [];
        }
    },

    async getConfigurations() {
        try {
            const res = await get(ServerEndpoint.ADMIN_CONFIG_LIST);
            return Array.isArray(res?.data?.data) ? res.data.data : [];
        } catch (error) {
            console.error('Failed to fetch configurations:', error);
            return [];
        }
    },

    async setConfiguration(key, value, label, description) {
        try {
            const res = await post(ServerEndpoint.ADMIN_CONFIG_SET, { key, value, label, description });
            return res?.data?.data || null;
        } catch (error) {
            console.error('Failed to set configuration:', error);
            return null;
        }
    },

    async deleteConfiguration(id) {
        try {
            const res = await post(ServerEndpoint.ADMIN_CONFIG_DELETE, { id });
            return res?.data?.data || null;
        } catch (error) {
            console.error('Failed to delete configuration:', error);
            return null;
        }
    },

    async getPrinters() {
        try {
            const res = await get(ServerEndpoint.ADMIN_PRINT_PRINTERS);
            const data = res?.data?.data;
            // Returns { printers: [], defaultPrinterId: number|null }
            return data && typeof data === 'object' && Array.isArray(data.printers)
                ? data
                : { printers: Array.isArray(data) ? data : [], defaultPrinterId: null };
        } catch (error) {
            console.error('Failed to fetch printers:', error);
            return { printers: [], defaultPrinterId: null };
        }
    },

    async printTestLabel(printerId) {
        try {
            const res = await post(ServerEndpoint.ADMIN_PRINT_TEST, { printerId });
            return res?.data?.data || null;
        } catch (error) {
            console.error('Failed to send test print:', error);
            return null;
        }
    },

    async printLabel({ labelUrl, orderId, copies = 1 }) {
        try {
            const res = await post(ServerEndpoint.ADMIN_PRINT_LABEL, { labelUrl, orderId, copies });
            return res?.data?.data || null;
        } catch (error) {
            console.error('Failed to print label:', error);
            return null;
        }
    },

    async updateOrderStatus(orderId, status) {
        try {
            const res = await patch(`${ServerEndpoint.ADMIN_ORDER_STATUS}/${orderId}/status`, { status });
            return res?.data?.data || null;
        } catch (error) {
            console.error('Failed to update order status:', error);
            return null;
        }
    },

    async downloadInvoice(orderId) {
        try {
            await downloadFile(`${ServerEndpoint.ADMIN_ORDER_INVOICE_PDF}/${orderId}/invoice/pdf`, `invoice-${orderId}.pdf`);
            return true;
        } catch (error) {
            console.error('Failed to download invoice:', error);
            return false;
        }
    },

    async buyOrderLabel(orderId) {
        try {
            const res = await post(`${ServerEndpoint.ADMIN_ORDERS}/${orderId}/buy-label`, {});
            return res?.data?.data || null;
        } catch (error) {
            console.error('Failed to buy order label:', error);
            return null;
        }
    },

    async getZohoCustomers() {
        try {
            const res = await get(ServerEndpoint.ADMIN_CUSTOMERS_ZOHO_LIST);
            return Array.isArray(res?.data?.data) ? res.data.data : [];
        } catch (error) {
            console.error('Failed to fetch Zoho customers:', error);
            return [];
        }
    },

    async getZohoCustomer(email) {
        try {
            const res = await get(`${ServerEndpoint.ADMIN_CUSTOMER_ZOHO}?email=${encodeURIComponent(email)}`);
            return res?.data?.data || null;
        } catch (error) {
            console.error('Failed to fetch Zoho customer:', error);
            return null;
        }
    },

};

export default AdminService;